import { AppLink } from 'app/_components/AppLink'
import { useFocus } from 'app/_components/Editor/useFocus'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'app/_components/Popover'
import { getBaseUrl } from 'lib/baseUrl'
import NextLink from 'next/link'
import { type JSX } from 'react'
import { type RenderElementProps, useReadOnly } from 'slate-react'
import { LinkForm } from './LinkForm'
import SharepointStreamEmbed from './SharepointStreamEmbed'

const MEDIA_EXTENSIONS = {
  video: ['.mp4', '.webm', '.ogg'],
}

const VIMEO_PATTERN = /vimeo\.com\/(?:video\/)?([0-9]+)/
// "Teilen" produces /:v:/g/ links, "Link kopieren" produces /:v:/r/ ones.
// Both resolve through the shares api.
const SHAREPOINT_PATTERN =
  /tbfag-my\.sharepoint\.com\/:v:\/[gr]\/personal\/[^/]+\/[^?]+/

export function isSharepointUrl(url: string): boolean {
  return SHAREPOINT_PATTERN.test(url)
}

function isVimeoUrl(url: string): boolean {
  return VIMEO_PATTERN.test(url)
}

function isMediaUrl(url: string): 'video' | null {
  const lowerUrl = url.toLowerCase()

  if (MEDIA_EXTENSIONS.video.some((ext) => lowerUrl.endsWith(ext))) {
    return 'video'
  }

  return null
}

export function isInternalLink(href: string) {
  if (href.startsWith('/')) return true

  try {
    const basePath = getBaseUrl()

    return href.startsWith(basePath)
  } catch (err) {
    return false
  }
}

export default function Link({
  attributes,
  children,
  element,
}: RenderElementProps) {
  const isReadOnly = useReadOnly()
  const focus = useFocus()

  if (element.type !== 'link') throw new Error('Unsupported file type')
  if (!element.url) {
    console.error('No URL found for element', element)
    return null
  }
  const mediaType = isMediaUrl(element.url)
  let link: JSX.Element | null

  function renderMedia(media: JSX.Element) {
    return (
      <span {...attributes} className="relative block w-full">
        {/* Keep Slate's text mounted so selection and focus can resolve it. */}
        <span className="sr-only">{children}</span>
        <span contentEditable={false} className="block">
          {media}
        </span>
      </span>
    )
  }

  if (isSharepointUrl(element.url)) {
    link = renderMedia(<SharepointStreamEmbed sharedUrl={element.url} />)
  } else if (isVimeoUrl(element.url)) {
    const id = element.url.match(VIMEO_PATTERN)?.[1]

    if (id) {
      link = renderMedia(
        <iframe
          src={`https://player.vimeo.com/video/${id}`}
          className="relative aspect-video h-full w-full rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />,
      )
    } else {
      link = (
        <AppLink
          {...attributes}
          className="button-text font-normal break-words"
          href={element.url as `/${string}`}
          target="_blank"
        >
          {children}
        </AppLink>
      )
    }
  } else if (mediaType === 'video') {
    link = renderMedia(
      <span className="my-4 block">
        <video src={element.url} controls className="max-w-full rounded-lg" />
      </span>,
    )
  } else if (isInternalLink(element.url)) {
    if (isReadOnly) {
      link = (
        <AppLink
          {...attributes}
          className="button-text font-normal break-words"
          href={element.url as `/${string}`}
        >
          {children}
        </AppLink>
      )
    } else {
      link = (
        <span
          {...attributes}
          className="button-text font-medium text-black underline"
        >
          {children}
        </span>
      )
    }
  } else {
    link = (
      <NextLink
        {...attributes}
        className="button-text font-normal break-words"
        href={element.url}
        target="_blank"
      >
        {children}
      </NextLink>
    )
  }

  // Assume no edit options when read-only
  if (isReadOnly) return link

  return (
    <Popover>
      <PopoverTrigger asChild>{link}</PopoverTrigger>
      <PopoverContent className="bg-gray-10 max-w-96" onCloseAutoFocus={focus}>
        <LinkForm />
      </PopoverContent>
    </Popover>
  )
}

Link.displayName = 'Link'
